import { Link, NavLink } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useCart } from "@/contexts/CartContext";
import { useTheme } from "@/contexts/ThemeContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel
} from "@/components/ui/dropdown-menu";
import { Moon, Sun, ShoppingBag, User, Heart, LogOut, LayoutDashboard, Library } from "lucide-react";

export default function Header() {
  const { user, logout } = useAuth();
  const { count } = useCart();
  const { theme, toggle } = useTheme();

  const navCls = ({ isActive }) =>
    `text-sm tracking-wide transition-colors hover:text-accent ${isActive ? "text-accent" : "text-foreground/80"}`;

  return (
    <header className="sticky top-0 z-40 border-b border-border bg-background/70 backdrop-blur-xl">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
        <Link to="/" data-testid="logo-link" className="font-serif text-xl md:text-2xl tracking-tight leading-none">
          BARON <span className="text-accent">BOOKS</span>
        </Link>
        <nav className="hidden md:flex items-center gap-8">
          <NavLink to="/catalogue" data-testid="nav-catalog" className={navCls}>Livres</NavLink>
          <NavLink to="/auteur" data-testid="nav-author" className={navCls}>À propos</NavLink>
          <NavLink to="/faq" data-testid="nav-faq" className={navCls}>FAQ</NavLink>
          <NavLink to="/contact" data-testid="nav-contact" className={navCls}>Contact</NavLink>
        </nav>
        <div className="flex items-center gap-2">
          <button data-testid="theme-toggle" onClick={toggle} aria-label="Thème"
            className="w-9 h-9 flex items-center justify-center rounded-sm border border-border hover:bg-secondary transition-colors">
            {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </button>
          <Link to="/wishlist" data-testid="nav-wishlist" className="w-9 h-9 hidden sm:flex items-center justify-center rounded-sm border border-border hover:bg-secondary transition-colors">
            <Heart className="w-4 h-4" />
          </Link>
          <Link to="/panier" data-testid="nav-cart" className="relative w-9 h-9 flex items-center justify-center rounded-sm border border-border hover:bg-secondary transition-colors">
            <ShoppingBag className="w-4 h-4" />
            {count > 0 && (
              <span data-testid="cart-count" className="absolute -top-2 -right-2 bg-accent text-accent-foreground text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center">
                {count}
              </span>
            )}
          </Link>
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="user-menu" className="w-9 h-9 flex items-center justify-center rounded-sm border border-border hover:bg-secondary">
                  <User className="w-4 h-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>{user.name || user.email}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="menu-library" asChild><Link to="/bibliotheque"><Library className="w-4 h-4 mr-2" />Ma bibliothèque</Link></DropdownMenuItem>
                <DropdownMenuItem data-testid="menu-orders" asChild><Link to="/commandes">Mes commandes</Link></DropdownMenuItem>
                <DropdownMenuItem data-testid="menu-wishlist" asChild><Link to="/wishlist">Favoris</Link></DropdownMenuItem>
                {user.role === "admin" && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem data-testid="menu-admin" asChild><Link to="/admin"><LayoutDashboard className="w-4 h-4 mr-2" />Tableau de bord</Link></DropdownMenuItem>
                  </>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="menu-logout" onClick={logout}><LogOut className="w-4 h-4 mr-2" />Se déconnecter</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Link to="/login">
              <Button data-testid="login-btn" variant="outline" size="sm" className="rounded-sm">Se connecter</Button>
            </Link>
          )}
        </div>
      </div>
    </header>
  );
}
